import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LoadingGrid, inr } from "@/components/bits";
import api, { errMsg } from "@/lib/api";
import { payWithPayU } from "@/lib/payu";

export default function PartnerGate({ children }) {
  const [overview, setOverview] = useState(null);
  const [payBusy, setPayBusy] = useState("");

  const load = () => api.get("/partner/overview").then(({ data }) => setOverview(data)).catch(() => setOverview({}));
  useEffect(() => {
    load();
  }, []);

  const buyPlan = async (plan) => {
    setPayBusy(plan.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "subscription", ref_id: plan.id, cycle: "yearly", origin_url: window.location.origin,
      });
      payWithPayU(data.payu_url, data.params);
    } catch (e) {
      toast.error(errMsg(e));
      setPayBusy("");
    }
  };

  if (!overview) return <LoadingGrid count={4} />;

  if (overview.locked) {
    return (
      <div data-testid="partner-locked">
        <div className="bg-white border border-border/60 rounded-2xl p-8 text-center max-w-2xl mx-auto mb-8">
          <div className="w-14 h-14 rounded-full bg-[#F5A623]/15 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-[#F5A623]" />
          </div>
          <h2 className="font-heading font-bold text-2xl text-[#0B1B3D]">Choose your partner plan</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Your dashboard unlocks the moment your subscription payment is confirmed.
            Both plans are valid for 1 year with unlimited candidate submissions.
          </p>
          {overview.partner?.status === "deactivated" && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-4" data-testid="partner-deactivated-note">
              Your account was deactivated by the admin team. Please contact support before renewing.
            </p>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {(overview.plans || []).map((p) => (
            <div
              key={p.id}
              className={`bg-white rounded-2xl p-7 border ${p.highlight ? "border-[#F5A623] shadow-lg shadow-[#F5A623]/10" : "border-border/60"}`}
              data-testid={`plan-card-${p.id}`}
            >
              {p.highlight && (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#F5A623] text-[#0B1B3D] rounded-full px-3 py-1">Best value</span>
              )}
              <h3 className="font-heading font-semibold text-lg text-[#0B1B3D] mt-2">{p.name}</h3>
              <p className="mt-3">
                <span className="font-heading font-bold text-3xl text-[#0B1B3D]">{inr(p.price_yearly)}</span>
                <span className="text-xs text-muted-foreground"> / year</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1" data-testid={`plan-total-${p.id}`}>
                + {p.gst_pct}% GST = <span className="font-semibold text-[#0B1B3D]">{inr(p.total)}</span> total
              </p>
              <ul className="mt-4 space-y-2">
                {(p.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <BadgeCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => buyPlan(p)}
                disabled={!!payBusy}
                className="w-full mt-6 rounded-full bg-[#0B1B3D] hover:bg-[#13264f]"
                data-testid={`buy-plan-${p.id}`}
              >
                {payBusy === p.id ? "Redirecting to PayU…" : `Pay ${inr(p.total)} & Activate`}
              </Button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return children(overview, load);
}
